/**
 * Krok GRAF2 — KARTY OBRAZOVKY „Přehled" NA JEDNOM MISTE.
 *
 * Predloha `/srv/debata/maklerov-grafika-v3/VYSLEDEK.png` (tmavy rezim) a
 * `…-prehled-svetly` (svetly) kresli dvanact karet. Jejich SEZNAM, VYCHOZI
 * PORADI podle role, vyber pro mobil a tvar dat jsou tady — ne zvlast na
 * serveru (`apps/api/src/routes/overview.ts`) a zvlast v administraci
 * (`apps/web-admin/src/components/overview-board.tsx`). Dve kopie teze pravdy
 * by se pri prvni nove karte rozesly (CLAUDE.md §8 pravidlo 2).
 *
 * Proc zod: tatáz definice slouzi jako SCHEMA ODPOVEDI endpointu
 * `GET /v1/overview` i jako TYP v administraci. Rucne psany DTO v
 * `apps/web-admin/src/lib/*-api.ts` by tise zestarl.
 *
 * JAZYK: tenhle modul nenese ani jeden text pro cloveka — jen klice a cisla.
 * Popisky karet jsou v `packages/platform/i18n/messages` (CLAUDE.md §1 bod 5).
 */
import { z } from 'zod';
/**
 * Poradi je POradi PREDLOHY, ne abeceda: `burning` je pruh pres celou sirku,
 * `nextStep` + `today` druhy radek, atd. Vychozi rozvrzeni z nej jen vybira.
 */
export const OVERVIEW_CARD_IDS = [
    'burning',
    'nextStep',
    'today',
    'cases',
    'demands',
    'portals',
    'radar',
    'commission',
    'officeSite',
    'adSpend',
    'brokers',
    'credits',
];
/**
 * CTYRI STAVY KAZDE KARTY (zadani GRAF2 „Pravidla" + krok PREHLED1):
 *  - `data`         — zdroj je napojeny a neco rika,
 *  - `empty`        — zdroj je napojeny, ale nic v nem (zadna poptavka, zadny tip),
 *  - `disconnected` — zdroj kancelar vubec nepripojila (statistiky portalu,
 *                     web kancelare, faktury za inzerci),
 *  - `error`        — vypocet TETO karty se nepovedl (rozesle se SQL se
 *                     schematem, nedostupna externi sluzba).
 *
 * Rozdil mezi `empty` a `disconnected` je ten duvod, proc karta nikdy
 * neukazuje „0" jako fakt: nula znamena „zmerili jsme nulu", ne „nemame co
 * merit". A `error` je tu proto, ze 4. 11. 2026 shodil jeden rozbity dotaz
 * (`crm_public_listings.published_at`, karta `portals`) CELY `GET /v1/overview`
 * na 500 — jedna vadna karta nesmi vzit makléřce celou obrazovku.
 */
export const OVERVIEW_CARD_STATES = ['data', 'empty', 'disconnected', 'error'];
const cardStateSchema = z.enum(OVERVIEW_CARD_STATES);
/** Penize vzdy jako numeric retezec + ISO 4217 (CLAUDE.md §3), nikdy `number`. */
const moneySchema = z.object({ amount: z.string(), currency: z.string().length(3) });
// ----------------------------------------------------------------- 1 Co hoří
const burningSchema = z.object({
    overdueInvoices: z.number().int().nonnegative(),
    contractsToSign: z.number().int().nonnegative(),
    /** Poptavky bez odpovedi dele nez 24 h (zadani: prah je 24 h). */
    demandsWithoutReply: z.number().int().nonnegative(),
});
// ------------------------------------------------------------- 2 Další krok
/**
 * Co to je za vec, aby administrace vedela, jaky POPISEK a jakou CESTU k ni
 * vzit. Server neposila text ani URL — jen druh a id, odkaz sklada router.
 *
 * Posledni dva druhy jsou ZALOHY, ne udalosti (prevzato z `routes/dnesek.tsx`,
 * kroky PIL1 V1 a V19, pri slouceni v GRAF2):
 *  - `resume`    — nic nehori, ale rozpracovana zakazka ceka; karta nabidne
 *                  tu NEJNOVEJI ZMENENOU,
 *  - `firstCase` — kancelar nema zakazku zadnou (prvni den po registraci);
 *                  „Nic na vás nečeká" by makléřku nechalo bez dalsiho kroku.
 */
export const NEXT_STEP_KINDS = [
    'contract',
    'invoice',
    'task',
    'demand',
    'resume',
    'firstCase',
];
const nextStepSchema = z.object({
    kind: z.enum(NEXT_STEP_KINDS),
    /** Id predmetu akce (smlouva, faktura, ukol, poptavka, zakazka). */
    targetId: z.string(),
    caseId: z.string().nullable(),
    propertyId: z.string().nullable(),
    /** Nazev zakazky / predmetu — udaj kancelare, ne nas text. */
    subject: z.string(),
    /**
     * Nahled nemovitosti (predloha ma na karte fotku pres celou sirku).
     * Kaskada je tataz jako u seznamu zakazek (`PropertyImage`): fotka →
     * mapa podle souradnic → veta „Fotka po nahrání". Proto jdou souradnice
     * s sebou; dopocet stredu obce se zamerne nedela (UX v4 § Pravidla 3).
     */
    coverUrl: z.string().nullable(),
    lat: z.number().nullable(),
    lon: z.number().nullable(),
    /** Faze zakazky u `resume` — administrace z ni sklada vetu „co chybí". */
    stage: z.string().nullable(),
});
// -------------------------------------------------------------------- 3 Dnes
const todaySchema = z.object({
    entries: z.array(z.object({
        id: z.string(),
        kind: z.enum(['meeting', 'task']),
        at: z.string().nullable(),
        title: z.string(),
        eventType: z.string().nullable(),
        color: z.string().nullable(),
        caseId: z.string().nullable(),
    })),
});
// --------------------------------------------------------- 4 Zakázky po fázích
const casesSchema = z.object({
    open: z.number().int().nonnegative(),
    byStage: z.array(z.object({ stage: z.string(), count: z.number().int().nonnegative() })),
});
// ------------------------------------------------- 5 Poptávky za 30 dní + cesta
const demandsSchema = z.object({
    last30: z.number().int().nonnegative(),
    /** Cesta poptávka → prohlídka → nabídka → rezervace nad týmž oknem 30 dní. */
    funnel: z.object({
        demands: z.number().int().nonnegative(),
        viewings: z.number().int().nonnegative(),
        offers: z.number().int().nonnegative(),
        reservations: z.number().int().nonnegative(),
    }),
});
// ------------------------------------------------------- 6 Inzerce na portálech
const portalsSchema = z.object({
    /** Tydny vzestupne; `weekStart` je pondeli (ISO datum). */
    weeks: z.array(z.object({
        weekStart: z.string(),
        byPortal: z.array(z.object({ portal: z.string(), views: z.number().int().nonnegative() })),
    })),
    /** Reakce = poptavka se `source: 'portal'`, rozpad podle portalu za tyden. */
    reactionsThisWeek: z.array(z.object({ portal: z.string(), reactions: z.number().int().nonnegative() })),
    /** „N nabídek bez reakce 14 dní" (predloha, cerveny pruh dole na karte). */
    withoutReaction14d: z.number().int().nonnegative(),
});
// ------------------------------------------------------- 7 Nové tipy z radaru
const radarSchema = z.object({ newLeads: z.number().int().nonnegative() });
// ------------------------------------------- 8 Provize tento měsíc proti cíli
const commissionSchema = z.object({
    thisMonth: moneySchema,
    /** Cil z Nastaveni kancelare; `null` = karta se kresli BEZ cary (zadani). */
    goal: moneySchema.nullable(),
    /** Mesice pro kridlovku na karte (vzestupne, `YYYY-MM`). */
    trend: z.array(z.object({ month: z.string(), amount: z.string() })),
});
// ----------------------------------------------------------- 9 Web kanceláře
const officeSiteSchema = z.object({
    /** Navstevy za 30 dni z Umami; `null` = web je, ale merení jeste neni. */
    visits30d: z.number().int().nonnegative().nullable(),
    demandsFromSite: z.number().int().nonnegative(),
});
// ------------------------------------ 10 Útrata za inzerci + cena za poptávku
const adSpendSchema = z.object({
    thisMonth: moneySchema,
    /** `null`, kdyz za mesic nebyla ani jedna poptavka — delit nulou nelze. */
    costPerDemand: moneySchema.nullable(),
});
// --------------------------------------------------------- 11 Výkon makléřů
const brokersSchema = z.object({
    items: z.array(z.object({
        userId: z.string(),
        name: z.string(),
        openCases: z.number().int().nonnegative(),
        commissionThisMonth: moneySchema,
    })),
});
// --------------------------------------------------- 12 Kredity a předplatné
const creditsSchema = z.object({
    items: z.array(z.object({ productCode: z.string(), balance: z.number().int() })),
    membershipExpiresAt: z.string().nullable(),
});
/**
 * Jedna karta odpovedi. `data` je `null` ve stavu `empty`, `disconnected`
 * i `error` — karta pak kresli pravdivou vetu a (u `disconnected`) nabidne
 * napojeni.
 *
 * Vycet je rozepsany schvalne: `OVERVIEW_CARD_IDS.map(...)` vraci pole, ne
 * n-tici, a `discriminatedUnion` by z nej ztratila typ zatizeni karty.
 */
const overviewCardSchema = z.discriminatedUnion('id', [
    z.object({ id: z.literal('burning'), state: cardStateSchema, data: burningSchema.nullable() }),
    z.object({ id: z.literal('nextStep'), state: cardStateSchema, data: nextStepSchema.nullable() }),
    z.object({ id: z.literal('today'), state: cardStateSchema, data: todaySchema.nullable() }),
    z.object({ id: z.literal('cases'), state: cardStateSchema, data: casesSchema.nullable() }),
    z.object({ id: z.literal('demands'), state: cardStateSchema, data: demandsSchema.nullable() }),
    z.object({ id: z.literal('portals'), state: cardStateSchema, data: portalsSchema.nullable() }),
    z.object({ id: z.literal('radar'), state: cardStateSchema, data: radarSchema.nullable() }),
    z.object({ id: z.literal('commission'), state: cardStateSchema, data: commissionSchema.nullable() }),
    z.object({ id: z.literal('officeSite'), state: cardStateSchema, data: officeSiteSchema.nullable() }),
    z.object({ id: z.literal('adSpend'), state: cardStateSchema, data: adSpendSchema.nullable() }),
    z.object({ id: z.literal('brokers'), state: cardStateSchema, data: brokersSchema.nullable() }),
    z.object({ id: z.literal('credits'), state: cardStateSchema, data: creditsSchema.nullable() }),
]);
export const overviewResponseSchema = z.object({
    /** Role, podle ktere server vybral VYCHOZI rozvrzeni (makler vs. majitel). */
    role: z.enum(['broker', 'owner']),
    /** Karty v poradi, ve kterem je ma uzivatel zobrazit (uz po jeho volbe). */
    cards: z.array(overviewCardSchema),
    /** Karty, ktere si uzivatel schoval — administrace je nabizi zpet. */
    hidden: z.array(z.enum(OVERVIEW_CARD_IDS)),
});
// --------------------------------------------------------------- rozvrzení
/** Vychozi karty maklere (zadani GRAF2: 1–7). */
export const DEFAULT_BROKER_CARDS = [
    'burning',
    'nextStep',
    'today',
    'cases',
    'demands',
    'portals',
    'radar',
];
/**
 * Majitel vidi navic 8–12 (zadani GRAF2: „Majitel navíc").
 *
 * Krok GRAF4 nalez 7 — PORADI JE ROZVRH PREDLOHY, ctene po radcich:
 * pruh `burning` (12/12) → `nextStep` + `today` (8 + 4) → **Zakázky ·
 * Provize · Poptávky** (4 + 4 + 4) → **Inzerce · Web · Útrata · Tipy**
 * (3 + 3 + 3 + 3). Do GRAF4 sly `demands` a `portals` pred `commission`,
 * takze druhy radek vysel 4 + 4 + 3 a treti zacinal uprostred rady.
 * `brokers` a `credits` predloha nekresli — stoji za ni.
 */
export const DEFAULT_OWNER_CARDS = [
    'burning',
    'nextStep',
    'today',
    'cases',
    'commission',
    'demands',
    'portals',
    'officeSite',
    'adSpend',
    'radar',
    'brokers',
    'credits',
];
/**
 * Mobil (zadani GRAF2: „Co hoří, Další krok, Dnes, Zakázky, Inzerce").
 * Neni to jine rozvrzeni — je to VYBER z toho uzivatelova: karta, kterou si
 * clovek schoval, se na telefonu neobjevi.
 */
export const MOBILE_OVERVIEW_CARDS = [
    'burning',
    'nextStep',
    'today',
    'cases',
    'portals',
];
/** `owner`/`admin` vidi ekonomiku kancelare, kazda dalsi role rozvrzeni maklere. */
export function overviewRoleOf(roles) {
    return roles.includes('owner') || roles.includes('admin') ? 'owner' : 'broker';
}
export const EMPTY_OVERVIEW_CARD_PREFS = { hidden: [], order: [] };
export const overviewCardPrefsSchema = z.object({
    hidden: z.array(z.enum(OVERVIEW_CARD_IDS)),
    order: z.array(z.enum(OVERVIEW_CARD_IDS)),
});
function isCardId(value) {
    return OVERVIEW_CARD_IDS.includes(value);
}
/** Jen znama id a kazde nejvys jednou — poradi vstupu zustava. */
function sanitize(values) {
    const seen = new Set();
    const out = [];
    for (const value of values) {
        if (!isCardId(value) || seen.has(value))
            continue;
        seen.add(value);
        out.push(value);
    }
    return out;
}
/**
 * Predvolba z ulozeneho JSON. Cokoli nerozpoznaneho (stara karta, rucne
 * zkazeny radek) = zadna predvolba, tedy vychozi rozvrzeni role — obrazovka
 * nikdy nespadne na ulozene hodnote.
 */
export function parseOverviewCardPrefs(raw) {
    if (raw === null || raw === undefined || raw === '')
        return EMPTY_OVERVIEW_CARD_PREFS;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return EMPTY_OVERVIEW_CARD_PREFS;
    }
    if (typeof parsed !== 'object' || parsed === null)
        return EMPTY_OVERVIEW_CARD_PREFS;
    const record = parsed;
    const hidden = Array.isArray(record.hidden) ? record.hidden.map(String) : [];
    const order = Array.isArray(record.order) ? record.order.map(String) : [];
    return { hidden: sanitize(hidden), order: sanitize(order) };
}
export function serializeOverviewCardPrefs(prefs) {
    return JSON.stringify({ hidden: [...sanitize(prefs.hidden)], order: [...sanitize(prefs.order)] });
}
/**
 * CO SE NAKONEC VYKRESLI A V JAKEM PORADI.
 *
 * Pravidla (jedno misto pro server i administraci):
 *  1. zaklad je vychozi sada ROLE — karta, kterou role nema videt (ekonomika
 *     kancelare u maklere), se neda zapnout ulozenou predvolbou,
 *  2. `order` posouva poradi, ale jen uvnitr sady role; co v `order` chybi,
 *     zustava za nim ve vychozim poradi (nova karta se tak objevi sama),
 *  3. `hidden` se odecte az nakonec.
 */
export function resolveOverviewLayout(input) {
    const allowed = input.role === 'owner' ? DEFAULT_OWNER_CARDS : DEFAULT_BROKER_CARDS;
    const allowedSet = new Set(allowed);
    const ordered = [];
    for (const id of input.prefs.order) {
        if (allowedSet.has(id))
            ordered.push(id);
    }
    for (const id of allowed) {
        if (!ordered.includes(id))
            ordered.push(id);
    }
    const hidden = input.prefs.hidden.filter((id) => allowedSet.has(id));
    return { visible: ordered.filter((id) => !hidden.includes(id)), hidden };
}
/** Karty, ktere se maji vykreslit na telefonu — prunik volby a mobilni sady. */
export function mobileOverviewCards(visible) {
    return visible.filter((id) => MOBILE_OVERVIEW_CARDS.includes(id));
}
//# sourceMappingURL=overview-cards.js.map